import * as api from "../clients/slashid-api/index"
import { OTPMethodOptions, TOTPMethodOptions } from "./options"

// Union of string values of the api.FactorMethod enum
type FactorMethodValues = `${Exclude<api.FactorMethod, "api" | "anonymous" | "direct_id" | "impersonate">}`

// Map from the string values above to method option types
class FactorMethodOptionsTypeMap implements Record<FactorMethodValues, any> {
    "webauthn": api.WebAuthnMethodOptions
    "oidc": api.OIDCMethodOptions
    "saml": api.SAMLMethodOptions
    "password": never

    "otp_via_sms": OTPMethodOptions | undefined
    "otp_via_email": OTPMethodOptions | undefined

    "totp": TOTPMethodOptions | undefined

    "email_link": never
    "sms_link": never
}

// Make Factor a union of objects of the form
// { method: X, options: [option type for X from FactorMethodOptionsTypeMap] }
type FactorTypeHelper<T> = {
    [K in keyof T]: {
        method: K
        options?: Omit<T[K], "method">
    }
}

// Convenient for autocompletion - this type is more accurate when compared to api.FactorMethod
export type FactorMethod = keyof FactorMethodOptionsTypeMap
export type Factor = FactorTypeHelper<FactorMethodOptionsTypeMap>[keyof FactorTypeHelper<FactorMethodOptionsTypeMap>]

export type PopupExtras = {
    setPopupUrl?: (url: string) => Window | null
    popup?: Window
}

export type OIDCWithExtras = {
    method: "oidc"
    options: Omit<api.OIDCMethodOptions, "method">
    extras: PopupExtras
}

export type SAMLWithExtras = {
    method: "saml"
    options: Omit<api.SAMLMethodOptions, "method">
    extras: PopupExtras
}

export type UserTokenText = string
export type TokenContainerText = string
export type OrganizationID = string

// Used to type anything that can be serialized to JSON. Do not return directly - rather specify an interface that extends this
export type JsonObject = { [Key in string]: JsonValue } & { [Key in string]?: JsonValue | undefined }
export type JsonArray = JsonValue[] | readonly JsonValue[]
export type JsonPrimitive = string | number | boolean | null
export type JsonValue = JsonPrimitive | JsonObject | JsonArray

/**
 * SlashID production environment name. Uses the following default values:
 *
 * const baseURL = "https://api.slashid.com"
 * const sdkURL = "https://cdn.slashid.com/sdk.html"
 */
export type ProductionEnvironment = "production"

/**
 * SlashID sandbox environment name. Uses the following default values:
 *
 * const baseURL = "https://api.sandbox.slashid.com"
 * const sdkURL = "https://cdn.sandbox.slashid.com/sdk.html"
 */
export type SandboxEnvironment = "sandbox"

/**
 * SlashID custom environment name. Values must be provided for baseURL and sdkURL.
 */
export type CustomEnvironment = {
    // API base URL
    baseURL: string
    // URL where the SDK is served
    sdkURL: string
}

export function isCustomEnvironment(environment: SlashIDEnvironment): environment is CustomEnvironment {
    return typeof environment === "object" && Boolean(environment.baseURL) && Boolean(environment.sdkURL)
}

export type SlashIDEnvironment = ProductionEnvironment | SandboxEnvironment | CustomEnvironment

// TODO enhance this
export type UICustomization = {
    [key: string]: any
}

export type ReachablePersonHandle = api.PersonHandle & { type: "email_address" | "phone_number" }
export type ReachablePersonHandleType = ReachablePersonHandle["type"]

export function isReachablePersonHandleType(handleType: api.PersonHandleType): handleType is ReachablePersonHandleType {
    return handleType === "email_address" || handleType === "phone_number"
}
